import {
  Button,
  DataTable,
  DataTableWC,
  Icon,
  PageBody,
  PageSection,
  Pagination,
  Spacer,
  Stack,
  Text,
  TextTitle,
} from '@pypestream/design-system';
import {
  dateFnsLocales,
  fallbackLanguage,
  TranslationComponent,
  useTranslation,
} from '@pypestream/translations';
import { formatAbsoluteTimeWithTimezone } from '@pypestream/utils';
import { html } from 'lit/static-html.js';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ListLoadStatus } from '../../components/list-load-status/list-load-status';
import { useUserTimeZone } from '../../hooks';
import { useRetryableListLoad } from '../../hooks/use-retryable-list-load';
import { normalizeHasuraTimezone } from '../../utils';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
} from '../../xstate/app.xstate';
import { IPage } from '../types';

const INITIAL_SKIP_PAGES = 0;
const INITIAL_PAGE_SIZE = 100;

export const LogsPage: React.FC<IPage> = ({ title }) => {
  const [skipPages, setSkipPages] = useState<number>(INITIAL_SKIP_PAGES);

  const isNewOrgLoading = useManagerStateMatches(
    'orgRelated.ready.orgs.loading'
  );

  const isReadyToLoad = useManagerStateMatches(
    'orgRelated.ready.currentOrg.selected'
  );
  const isLoaded = useManagerStateMatches('orgRelated.ready.logs.loaded');
  const isLoading = useManagerStateMatches('orgRelated.ready.logs.loading');
  const isFailed = useManagerStateMatches('orgRelated.ready.logs.error');

  const { defaultLanguage, auditLogs } = useManagerCtxSelector((ctx) => ({
    orgId: ctx.selectedOrgId,
    auditLogs: ctx.logs,
    defaultLanguage: ctx.userInfo?.defaultLanguage,
  }));

  const { rows: logs, count } = auditLogs;

  const tableRef = useRef<DataTableWC>(null);
  const [t] = useTranslation();
  const userTimeZone = useUserTimeZone();

  const fetchLogsData = useCallback(
    (skip: number, size: number, invalidateCache: boolean = false) => {
      sendManagerEvent({
        type: 'manager.logs.loadLogs',
        queryMetadata: {
          skipPages: skip,
          pageSize: size,
        },
        invalidateCache: invalidateCache,
      });
    },
    []
  );

  const onPaginationChange = useCallback(
    (_index: number, _size: number, invalidateCache: boolean = false) => {
      // The first page is 0, but the first page in the pagination is 1, also prevent initial render if the index is 0
      const skip = _index === 0 ? 0 : _index - 1;

      setSkipPages(skip);
      fetchLogsData(skip, _size, invalidateCache);
    },
    [fetchLogsData]
  );

  // Fetch logs when the page is loaded and there are no logs
  useEffect(() => {
    if (isReadyToLoad && (!logs || !logs.length)) {
      fetchLogsData(INITIAL_SKIP_PAGES, INITIAL_PAGE_SIZE);
    }
  }, [isReadyToLoad, fetchLogsData, logs]);

  // Reset the skip pages when a new org is loaded
  useEffect(() => {
    if (isNewOrgLoading) {
      onPaginationChange(INITIAL_SKIP_PAGES, INITIAL_PAGE_SIZE);
    }
  }, [isNewOrgLoading, onPaginationChange]);

  // PSTUDIO-4442 S38. A failed load parks `logs` in `error`, and the page
  // mapped "not loaded" to the loader for good. The retry asks again for the
  // page the user was on; a failed load writes nothing to `logsCache`
  // (`onErrorAssignContext` only records the error), so it reaches the API.
  const requestLoad = useCallback(
    () => fetchLogsData(skipPages, INITIAL_PAGE_SIZE),
    [fetchLogsData, skipPages]
  );
  const load = useRetryableListLoad({
    loaded: isLoaded,
    failed: isFailed,
    requestLoad,
  });

  const pagination = useMemo(
    () =>
      logs && logs.length ? (
        <Pagination
          total={count}
          pageSize={INITIAL_PAGE_SIZE}
          currentPage={skipPages + 1}
          onChange={({ detail: { page, pageSize: _pageSize } }) =>
            onPaginationChange(page, _pageSize)
          }
          onShowSizeChange={({ detail: { page, pageSize: _pageSize } }) =>
            onPaginationChange(page, _pageSize)
          }
          showPageSizeSelector={false}
        />
      ) : null,
    [count, logs, onPaginationChange, skipPages]
  );

  return (
    <PageBody background="none">
      <PageSection size="small">
        <>
          <Stack alignItems="center" justifyContent="space-between">
            <TextTitle size="xsmall">
              <TranslationComponent i18nKey="manager/logs:title">
                {title}
              </TranslationComponent>
            </TextTitle>
            <Stack
              justifyContent="end"
              alignItems="center"
              nowrap
              style={{ flex: 1 }}
            >
              {pagination}
              <Button
                size="large"
                onClick={() =>
                  onPaginationChange(
                    INITIAL_SKIP_PAGES,
                    INITIAL_PAGE_SIZE,
                    true
                  )
                }
                disabled={isLoading}
              >
                <Icon slot="prefix" name="refresh" size="xsmall" />
                Refresh
              </Button>
            </Stack>
          </Stack>
          <Spacer size="large" />
          <ListLoadStatus
            load={load}
            testIdPrefix="logs"
            texts={{
              title:
                t('manager/logs:loadError.title', {
                  defaultValue: "We couldn't load the audit logs",
                }) || '',
              body:
                t('manager/logs:loadError.body', {
                  defaultValue: 'Something went wrong while loading this list.',
                }) || '',
              retry:
                t('manager/logs:loadError.retry', {
                  defaultValue: 'Try again',
                }) || '',
              retrying:
                t('manager/logs:loadError.retrying', {
                  defaultValue: 'Retrying…',
                }) || '',
              recovered:
                t('manager/logs:loadError.recovered', {
                  defaultValue: 'Audit logs loaded',
                }) || '',
            }}
          />
          {load.view === 'ready' && (
            // A plain div: the DS wrappers do not accept `tabIndex`, and focus
            // has to land on a native node after a successful retry.
            <div ref={load.recoveredSectionRef} tabIndex={-1}>
              <DataTable
                size="small"
                data={logs}
                ref={tableRef}
                columns={[
                  {
                    accessorKey: 'action',
                    header: () =>
                      t('manager/logs:table.column1', {
                        defaultValue: 'Action',
                      }) || '',
                    cell: ({ row, renderValue }) => {
                      return html`
                        <ps-text-body
                          variant="secondary"
                          style="max-width: 400px;"
                          >${row.original?.auditLogDetail
                            ?.displayMessage}</ps-text-body
                        >
                      `;
                    },
                  },
                  {
                    accessorKey: 'user',
                    accessorFn: (row) =>
                      `${row.user?.firstName || ''} ${row.user?.lastName || ''} ${row.user?.userName || ''}`,
                    header: () =>
                      t('manager/logs:table.column2', {
                        defaultValue: 'User',
                      }) || '',
                    cell: ({ row, renderValue }) => {
                      return html`<ps-stack
                        gutter="xsmall"
                        display="inline-flex"
                        direction="row"
                        nowrap
                        alignItems="center"
                      >
                        <ps-avatar
                          label="${row.original?.user?.firstName} ${row.original
                            ?.user?.lastName}"
                          src="${row.original?.user?.picture || ''}"
                        ></ps-avatar>
                        <div>
                          <ps-text-subtitle size="small"
                            >${row.original?.user?.firstName}
                            ${row.original?.user?.lastName}</ps-text-subtitle
                          >
                          <ps-text-caption
                            >${row.original?.user?.userName}</ps-text-caption
                          >
                        </div>
                      </ps-stack>`;
                    },
                  },
                  {
                    accessorKey: 'when',
                    header: () =>
                      t('manager/logs:table.column3', {
                        defaultValue: 'When',
                      }) || '',
                    // e2e deferred: Manager e2e runs against the deployed target (main.pypestream.dev), not this PR's code, so it can't assert the new absolute format until deploy. Logic is covered by unit tests (formatAbsoluteTimeWithTimezone + normalizeHasuraTimezone). Follow-up: logs e2e infra ticket.
                    cell: ({ row }) =>
                      html`<ps-text-body variant="secondary" textAlign="end">
                        ${formatAbsoluteTimeWithTimezone({
                          // PSTUDIO-3416: Hasura returns createdAt WITHOUT a 'Z' — normalize to UTC so parseISO doesn't read it as browser-local time and shift it by the browser offset (same pattern as project-item / team-users).
                          fromDate: normalizeHasuraTimezone(
                            row.original.createdAt
                          ),
                          locale:
                            dateFnsLocales[defaultLanguage || fallbackLanguage],
                          timeZone: userTimeZone,
                        })}
                      </ps-text-body>`,
                    meta: {
                      align: 'end',
                    },
                  },
                ]}
              />
              <Spacer size="xlarge" />
              {!logs ||
                (!logs.length && (
                  <Text
                    size="xsmall"
                    textAlign="center"
                    className="u-margin-top-xlarge"
                    i18nKey="manager/logs:empty"
                  >
                    No data.
                  </Text>
                ))}
              {pagination}
            </div>
          )}
        </>
      </PageSection>
    </PageBody>
  );
};
